/** @format — List row action with portaled design tooltip */

import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

function TooltipArrow({ className }: { className?: string }) {
  return (
    <svg
      width={12}
      height={5}
      viewBox="0 0 12 5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        d="M5.38117 0.229062L0 4.67438H11.5L6.69426 0.263313C6.32647 -0.0742683 5.76606 -0.0888867 5.38117 0.229062Z"
        fill="black"
      />
    </svg>
  );
}

function PortaledTooltip({
  anchorRef,
  label,
  placement,
  visible,
}: {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  label: string;
  placement: "top" | "bottom";
  visible: boolean;
}) {
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: "hidden" });

  useLayoutEffect(() => {
    if (!visible || !anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;

    if (placement === "top") {
      setStyle({
        position: "fixed",
        left: centerX,
        top: rect.top - 6,
        transform: "translate(-50%, -100%)",
        zIndex: 9999,
      });
      return;
    }

    setStyle({
      position: "fixed",
      left: centerX,
      top: rect.bottom + 6,
      transform: "translateX(-50%)",
      zIndex: 9999,
    });
  }, [anchorRef, placement, visible]);

  if (!visible) return null;

  return createPortal(
    <div
      className="pointer-events-none flex flex-col items-center"
      style={style}
      role="tooltip"
    >
      {placement === "bottom" ? (
        <>
          <TooltipArrow />
          <div className="whitespace-nowrap rounded-lg bg-black px-2 py-1.5 text-xs text-white">
            {label}
          </div>
        </>
      ) : (
        <>
          <div className="whitespace-nowrap rounded-lg bg-black px-2 py-1.5 text-xs text-white">
            {label}
          </div>
          <TooltipArrow className="rotate-180" />
        </>
      )}
    </div>,
    document.body,
  );
}

export default function DocumentListActionButton({
  label,
  onClick,
  children,
  tooltipPlacement = "top",
}: {
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  tooltipPlacement?: "top" | "bottom";
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);

  const showTooltip = useCallback(() => setHovered(true), []);
  const hideTooltip = useCallback(() => setHovered(false), []);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        className="flex items-center justify-center rounded-lg p-2 text-[#0a0a0a] outline-none ring-0 hover:bg-neutral-100 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
        onClick={onClick}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {children}
      </button>
      <PortaledTooltip
        anchorRef={buttonRef}
        label={label}
        placement={tooltipPlacement}
        visible={hovered}
      />
    </>
  );
}
